/**
 * @file Page d'acceptation d'invitation.
 * Gère les jetons d'invitation de cercle : valide le jeton, affiche les
 * informations du cercle et permet à l'utilisateur d'accepter l'invitation
 * pour rejoindre le cercle.
 */
import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useApp } from '../contexts/AppContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Loader2, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { getErrorMessage } from '../lib/errorMessages';
import { buildAuthLink } from '../lib/authRedirect';
import { APP_ROUTES } from '../lib/routes';

export default function InviteAccept() {
  const { token } = useParams();
  const { t } = useTranslation();
  const { user, loading: authLoading } = useAuth();
  const { refreshCircles } = useApp();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const invitePath = APP_ROUTES.inviteAccept(token);
  const loginLink = buildAuthLink(APP_ROUTES.login, invitePath);
  const registerLink = buildAuthLink(APP_ROUTES.register, invitePath);

  useEffect(() => {
    api.get(`/invites/info/${token}/`).then(r => setInfo(r.data)).catch((err) => setError(getErrorMessage(err, t('invite.invalid_message')))).finally(() => setLoading(false));
  }, [token, t]);

  const acceptInvite = useCallback(async () => {
    if (accepting) return;
    setAccepting(true); setError('');
    try {
      const r = await api.post(`/invites/accept/${token}/`);
      setResult(r.data);
      await refreshCircles();
    } catch (e) {
      setError(getErrorMessage(e, t('invite.accept_failed')));
    } finally {
      setAccepting(false);
    }
  }, [accepting, refreshCircles, t, token]);

  useEffect(() => {
    if (loading || authLoading || !user || !info?.valid || result || error) return;
    void acceptInvite();
  }, [acceptInvite, authLoading, error, info, loading, result, user]);

  const handleAccept = () => {
    void acceptInvite();
  };

  if (loading || authLoading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="min-h-screen flex items-center justify-center bg-background" data-testid="invite-accept-page">
      <div className="w-full max-w-sm px-6 text-center">
        {result ? (
          <div className="animate-fade-in">
            <CheckCircle className="h-12 w-12 text-green-600 mx-auto" />
            <h1 className="text-2xl font-medium tracking-tight text-foreground mt-4" style={{ fontFamily: 'Outfit' }}>{t('invite.welcome_title')}</h1>
            <p className="text-sm text-muted-foreground mt-2">{t('invite.joined_message', { circle_name: result.circle?.name })}</p>
            <Button asChild className="mt-6 rounded-sm bg-primary text-primary-foreground hover:bg-primary/90" data-testid="go-dashboard-btn">
              <Link to={APP_ROUTES.dashboard}>{t('invite.go_dashboard')}</Link>
            </Button>
          </div>
        ) : error ? (
          <div className="animate-fade-in">
            <XCircle className="h-12 w-12 text-red-500 mx-auto" />
            <h1 className="text-2xl font-medium tracking-tight text-foreground mt-4" style={{ fontFamily: 'Outfit' }}>{t('invite.error_title')}</h1>
            <p className="text-sm text-red-600 mt-2">{error}</p>
            <Button asChild variant="outline" className="mt-6 rounded-sm" data-testid="invite-home-btn">
              <Link to={APP_ROUTES.home}>{t('invite.go_home')}</Link>
            </Button>
          </div>
        ) : info?.valid ? (
          <div className="animate-fade-in">
            <AlertCircle className="h-12 w-12 text-blue-600 mx-auto" />
            <h1 className="text-2xl font-medium tracking-tight text-foreground mt-4" style={{ fontFamily: 'Outfit' }}>
              {t('invite.invited_title')}
            </h1>
            <p className="text-sm text-muted-foreground mt-2">
              {t('invite.join_circle', { circle_name: info.circle_name })}
            </p>
            {!user ? (
              <div className="mt-6 space-y-2">
                <p className="text-xs text-muted-foreground">{t('invite.signin_first')}</p>
                <Button asChild className="w-full rounded-sm bg-primary text-primary-foreground hover:bg-primary/90" data-testid="invite-login-btn">
                  <Link to={loginLink}>{t('common.sign_in')}</Link>
                </Button>
                <Button asChild variant="outline" className="w-full rounded-sm" data-testid="invite-register-btn">
                  <Link to={registerLink}>{t('home.create_account')}</Link>
                </Button>
              </div>
            ) : (
              <Button onClick={handleAccept} className="mt-6 rounded-sm bg-primary text-primary-foreground hover:bg-primary/90" disabled={accepting} data-testid="invite-accept-btn">
                {accepting ? <><Loader2 className="h-4 w-4 animate-spin" /> <span>{t('invite.accepting')}</span></> : t('invite.accept')}
              </Button>
            )}
          </div>
        ) : (
          <div className="animate-fade-in">
            <XCircle className="h-12 w-12 text-muted-foreground mx-auto" />
            <h1 className="text-2xl font-medium tracking-tight text-foreground mt-4" style={{ fontFamily: 'Outfit' }}>{t('invite.invalid_title')}</h1>
            <p className="text-sm text-muted-foreground mt-2">{t('invite.invalid_message')}</p>
          </div>
        )}
      </div>
    </div>
  );
}
